// IE Hub - Select Field Component
// Enhanced select input with dialog-based options support and validation

import React, {
  forwardRef,
  useState,
  useMemo,
  useImperativeHandle,
  useEffect,
  useRef,
} from "react";
import {
  Box,
  Chip,
  FormControl,
  OutlinedInput,
  Typography,
  Dialog,
  TextField,
  IconButton,
  Slide,
  useTheme,
  useMediaQuery,
  alpha,
  styled,
  InputAdornment,
  Button,
  DialogActions,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { TransitionProps } from "@mui/material/transitions";
import RadioGroup from "./RadioGroup";
import CheckboxGroup from "./CheckboxGroup";
import ConditionalContainer from "../layout/ConditionalContainer";

interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
  group?: string;
  description?: string;
}

interface ValidationRules {
  required?: boolean;
  minSelections?: number;
  maxSelections?: number;
  requiredMessage?: string;
}

interface SelectProps {
  label?: string;
  // Optional typography variant for the label shown above the field.
  // Defaults to 'subtitle1' to preserve existing behaviour.
  labelVariant?: "subtitle1" | "body1" | "body2";
  // Optional label to show in the selection dialog header. Falls back to label
  btnLabel?: string | undefined;
  dialogLabel?: string | undefined;
  options: SelectOption[];
  value?: string | string[] | number | number[];
  onChange?: (value: string | string[] | number | number[]) => void;
  error?: boolean;
  helperText?: string | undefined;
  validation?: ValidationRules;
  onValidate?: (isValid: boolean, error: string) => void;
  placeholder?: string;
  showValidationIcon?: boolean;
  clearable?: boolean;
  searchable?: boolean;
  groupBy?: boolean;
  multiple?: boolean;
  fullWidth?: boolean;
  size?: "small" | "medium";
  disabled?: boolean;
  name?: string;
  // When true, suppress local validation and rely on parent (RHF) errors on submit
  deferValidation?: boolean;
  // Custom UI blocks like TextField
  helperTop?: string | undefined; // Above input
  helperAttached?: string | undefined; // Attached block inside container
  helperBottom?: string | undefined; // Below input
  helperBottomTone?: "default" | "error"; // Styling hint for helperBottom
  showErrorBelow?: boolean; // controls whether error renders beneath input
  // Custom free-text entry when option not found
  allowCustomEntry?: boolean;
  customEntryLinkText?: string;
  customEntryPlaceholder?: string;
  customEntryConfirmText?: string;
  customEntryCancelText?: string;
  // ConditionalContainer integration
  title?: string;
  description?: string;
  containerVariant?: "paper" | "outlined" | "plain";
  showErrorInContainer?: boolean;
  // LogRocket privacy masking - when true, adds data-private attribute to mask PII in recordings
  // Defaults to true for security; set to false for non-PII fields
  dataPrivate?: boolean;
}

// Slide up transition for mobile
const SlideUpTransition = React.forwardRef<
  unknown,
  TransitionProps & { children: React.ReactElement }
>((props, ref) => {
  const { appear, enter, exit, ...otherProps } = props;
  return (
    <Slide
      direction="up"
      ref={ref}
      appear={appear ?? true}
      enter={enter ?? true}
      exit={exit ?? true}
      {...otherProps}
    />
  );
});

// Styled components
const Wrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
}));

const AttachedBlock = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: theme.spacing(1.25, 1.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor:
    theme.palette.mode === "light"
      ? theme.palette.grey[100]
      : theme.palette.grey[900],
  border: `1px solid ${alpha(theme.palette.text.primary, 0.15)}`,
  color: theme.palette.text.secondary,
  fontSize: "0.875rem",
}));

const StyledFormControl = styled(FormControl)<{ error?: boolean }>(
  ({ theme, error }) => ({
    "& .MuiOutlinedInput-root": {
      height: "44px", // Set explicit height to 44px to match TextField
      cursor: "pointer",
      transition: "all 0.2s ease-in-out",
      backgroundColor: theme.palette.background.paper,

      // Default border styling
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.divider,
        borderWidth: "1px",
      },

      // Error state styling - highest priority
      ...(error && {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: `${theme.palette.error.main} !important`,
          borderWidth: "2px !important",
        },
      }),

      "&:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 0.02),
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: error
            ? `${theme.palette.error.main} !important`
            : theme.palette.primary.main,
          borderWidth: "2px !important",
        },
      },

      "&.Mui-focused": {
        backgroundColor: theme.palette.background.paper,
        boxShadow: `0 0 0 2px ${alpha(error ? theme.palette.error.main : theme.palette.primary.main, 0.2)}`,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: error
            ? `${theme.palette.error.main} !important`
            : `${theme.palette.primary.main} !important`,
          borderWidth: "2px !important",
        },
      },

      // MUI error class - ensure it works
      "&.Mui-error": {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: `${theme.palette.error.main} !important`,
          borderWidth: "2px !important",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: `${theme.palette.error.main} !important`,
          borderWidth: "2px !important",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: `${theme.palette.error.main} !important`,
          borderWidth: "2px !important",
        },
      },

      "&.Mui-disabled": {
        backgroundColor: alpha(theme.palette.action.disabled, 0.05),
        cursor: "not-allowed",
      },
    },

    "& .MuiInputLabel-outlined": {
      "&.Mui-focused": {
        color: error ? theme.palette.error.main : theme.palette.primary.main,
      },
      "&.Mui-error": {
        color: theme.palette.error.main,
      },
    },

    "& .MuiFormHelperText-root": {
      marginLeft: 0,
      marginTop: theme.spacing(0.75),
      fontSize: "0.8125rem",
      transition: "color 0.2s ease-in-out",
    },
  })
);

const StyledDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== "isMobile" && prop !== "keyboardHeight",
})<{ isMobile?: boolean; keyboardHeight?: number }>(
  ({ theme, isMobile, keyboardHeight = 0 }) => ({
    "& .MuiDialog-paper": {
      ...(isMobile && {
        margin: 0,
        width: "100%",
        maxWidth: "100%",
        // Adjust maxHeight when keyboard is visible to prevent content from going behind keyboard
        maxHeight:
          keyboardHeight > 0 ? `calc(100% - ${keyboardHeight}px)` : "100%",
        borderRadius: `${theme.spacing(2)} ${theme.spacing(2)} 0 0`,
        position: "fixed",
        bottom: keyboardHeight > 0 ? keyboardHeight : 0,
        transition: "max-height 0.15s ease-out, bottom 0.15s ease-out",
      }),
      ...(!isMobile && {
        borderRadius: theme.spacing(2),
        minWidth: "400px",
        maxWidth: "500px",
        maxHeight: "80vh",
        margin: theme.spacing(2),
      }),
    },
  })
);

const DialogHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isMobile",
})<{ isMobile?: boolean }>(({ theme, isMobile }) => ({
  display: "flex",
  alignItems: "center",
  borderBottom: `1px solid ${theme.palette.divider}`,
  ...(isMobile && {
    padding: theme.spacing(1, 2, 1, 2),
    paddingTop: theme.spacing(1),
    "&::before": {
      content: '""',
      position: "absolute",
      top: theme.spacing(1),
      left: "50%",
      transform: "translateX(-50%)",
      width: "32px",
      height: "4px",
      backgroundColor: theme.palette.divider,
      borderRadius: "2px",
    },
  }),
  ...(!isMobile && {
    padding: theme.spacing(1.25, 1.5),
    minHeight: "48px",
  }),
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2.5, 1.5, 2.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const OptionsContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.5, 1.5, 1, 1.5),
  maxHeight: "400px",
  overflowY: "auto",
}));

/**
 * Hook to track keyboard height using visualViewport API
 * Returns the keyboard height (difference between window height and visual viewport height)
 */
const useKeyboardHeight = (isActive: boolean): number => {
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const initialViewportHeight = useRef<number | null>(null);

  useEffect(() => {
    if (!isActive) {
      setKeyboardHeight(0);
      return;
    }

    const visualViewport = window.visualViewport;
    if (!visualViewport) {
      return;
    }

    // Store initial viewport height on first activation
    if (initialViewportHeight.current === null) {
      initialViewportHeight.current = visualViewport.height;
    }

    const handleResize = () => {
      // Calculate keyboard height as the difference between window inner height and visual viewport height
      // This accounts for the on-screen keyboard taking up space
      const windowHeight = window.innerHeight;
      const viewportHeight = visualViewport.height;
      const offset = visualViewport.offsetTop;

      // The keyboard height is the difference between the window height and the visual viewport
      // accounting for any scroll offset
      const calculatedKeyboardHeight = Math.max(
        0,
        windowHeight - viewportHeight - offset
      );

      // Only set keyboard height if it's significant (> 100px to avoid false positives)
      // and we're on a mobile device
      if (calculatedKeyboardHeight > 100) {
        setKeyboardHeight(calculatedKeyboardHeight);
      } else {
        setKeyboardHeight(0);
      }
    };

    // Initial check
    handleResize();

    visualViewport.addEventListener("resize", handleResize);
    visualViewport.addEventListener("scroll", handleResize);

    return () => {
      visualViewport.removeEventListener("resize", handleResize);
      visualViewport.removeEventListener("scroll", handleResize);
    };
  }, [isActive]);

  return keyboardHeight;
};

const SelectTrigger = styled(OutlinedInput)<{ error?: boolean }>(
  ({ theme, error }) => ({
    cursor: "pointer",
    "& .MuiOutlinedInput-input": {
      cursor: "pointer",
    },
    "&:hover": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: error
          ? theme.palette.error.main
          : theme.palette.primary.main,
      },
    },
  })
);

interface SelectHandle {
  open: () => void;
  close: () => void;
}

const Select = forwardRef<SelectHandle, SelectProps>(
  (
    {
      label: fieldLabel,
      labelVariant = "subtitle1",
      btnLabel,
      dialogLabel,
      options,
      value = "",
      onChange,
      error = false,
      helperText: _helperText,
      validation = {},
      onValidate,
      placeholder = "Select an option",
      clearable = false,
      multiple = false,
      searchable = true,
      fullWidth = true,
      size = "medium",
      disabled = false,
      name,
      deferValidation = false,
      // Custom UI blocks
      helperTop,
      helperAttached,
      helperBottom,
      helperBottomTone = "default",
      // ConditionalContainer props
      title,
      description,
      containerVariant = "plain",
      // custom entry props
      allowCustomEntry = false,
      customEntryLinkText = "Can't find my referring company",
      customEntryPlaceholder = "Enter company name",
      customEntryConfirmText = "Use this company",
      customEntryCancelText = "Back to list",
      // LogRocket privacy masking - defaults to true
      dataPrivate = true,
    },
    ref
  ) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [validationError, setValidationError] = useState("");
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    // Track keyboard height for mobile keyboard avoidance
    const keyboardHeight = useKeyboardHeight(isDialogOpen && isMobile);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [tempValue, setTempValue] = useState<
      string | string[] | number | number[]
    >(value);
    const [customEntryMode, setCustomEntryMode] = useState(false);
    const [customEntryText, setCustomEntryText] = useState("");

    // Filter options based on search term
    const filteredOptions = useMemo(() => {
      if (!searchTerm) return options;

      return options.filter(
        (option) =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          option.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }, [options, searchTerm]);

    // Convert options to RadioGroup format
    const radioOptions = useMemo(() => {
      return filteredOptions.map((option) => ({
        value: option.value,
        title: option.label,
        description: option.description,
        disabled: option.disabled,
      }));
    }, [filteredOptions]);

    // Convert options to CheckboxGroup format for multiselect
    const checkboxOptions = useMemo(() => {
      return filteredOptions.map((option) => ({
        value: option.value,
        title: option.label,
        description: option.description,
        disabled: option.disabled,
      }));
    }, [filteredOptions]);

    // Validation logic
    const validateInput = (selectedValue: any) => {
      let errorMsg = "";

      if (validation.required) {
        if (multiple) {
          if (
            !selectedValue ||
            (Array.isArray(selectedValue) && selectedValue.length === 0)
          ) {
            errorMsg =
              validation.requiredMessage || "Please select at least one option";
          }
        } else {
          if (!selectedValue || selectedValue === "") {
            errorMsg = validation.requiredMessage || "This field is required";
          }
        }
      }

      if (multiple && Array.isArray(selectedValue)) {
        if (
          validation.minSelections &&
          selectedValue.length < validation.minSelections
        ) {
          errorMsg = `Select at least ${validation.minSelections} options`;
        }

        if (
          validation.maxSelections &&
          selectedValue.length > validation.maxSelections
        ) {
          errorMsg = `Select at most ${validation.maxSelections} options`;
        }
      }

      if (!deferValidation) {
        setValidationError(errorMsg);

        if (onValidate) {
          onValidate(errorMsg === "", errorMsg);
        }
      }

      return errorMsg === "";
    };

    // Handle dialog open
    const handleDialogOpen = () => {
      if (disabled) return;
      setTempValue(value);
      setIsDialogOpen(true);
      setSearchTerm("");
      setCustomEntryMode(false);
      setCustomEntryText("");
    };

    // Handle dialog close
    const handleDialogClose = () => {
      setIsDialogOpen(false);
      setSearchTerm("");
      setCustomEntryMode(false);
      setCustomEntryText("");

      // For multiselect, revert tempValue to original value if cancelled
      if (multiple) {
        setTempValue(value);
      }

      // Validate on close (only if not deferring)
      if (!deferValidation) {
        validateInput(value);
      }
    };

    useImperativeHandle(ref, () => ({
      open: () => handleDialogOpen(),
      close: () => handleDialogClose(),
    }));

    // Handle confirm custom entry
    const handleConfirmCustomEntry = () => {
      if (!customEntryText) return;
      // call onChange with the raw text
      if (!deferValidation && Object.keys(validation).length > 0) {
        validateInput(customEntryText);
      }
      if (onChange) {
        onChange(customEntryText);
      }
      setIsDialogOpen(false);
      setSearchTerm("");
      setCustomEntryMode(false);
    };

    // Handle option selection in dialog
    const handleOptionSelect = (
      selectedValue: string | number | Array<string | number>
    ) => {
      if (multiple) {
        // Handle multiple selection - selectedValue will be an array from CheckboxGroup
        const arrayValue = Array.isArray(selectedValue)
          ? selectedValue
          : [selectedValue];
        setTempValue(arrayValue as string[] | number[]);

        // Don't call onChange immediately for multiselect - wait for Apply button
        // Don't close dialog for multiple select - let user continue selecting
      } else {
        const singleValue = Array.isArray(selectedValue)
          ? selectedValue[0]
          : selectedValue;
        if (singleValue !== undefined) {
          setTempValue(singleValue);

          // Validate locally only if not deferring
          if (!deferValidation && Object.keys(validation).length > 0) {
            validateInput(singleValue);
          }

          // Call onChange
          if (onChange) {
            onChange(singleValue);
          }

          // Close dialog after selection for single select
          setIsDialogOpen(false);
          setSearchTerm("");
        }
      }
    };

    // Clear search
    const handleClearSearch = () => {
      setSearchTerm("");
    };

    const hasSelectedValue = multiple
      ? Array.isArray(value) && value.length > 0
      : value !== "" && value !== null && value !== undefined;

    const handleClearValue = (event: React.MouseEvent) => {
      event.stopPropagation();
      if (disabled) return;
      const clearedValue = multiple ? [] : "";
      if (!deferValidation && Object.keys(validation).length > 0) {
        validateInput(clearedValue);
      }
      if (onChange) {
        onChange(clearedValue);
      }
    };

    // Handle apply for multiselect
    const handleApplyMultiselect = () => {
      // Validate final selection only if not deferring
      if (!deferValidation && Object.keys(validation).length > 0) {
        validateInput(tempValue);
      }

      // Apply the tempValue to the actual value
      if (onChange) {
        onChange(tempValue);
      }

      // Close dialog
      setIsDialogOpen(false);
      setSearchTerm("");
    };

    // Get display value for text
    const getDisplayText = () => {
      if (multiple && Array.isArray(value)) {
        if (value.length === 0) {
          return placeholder;
        }
        return `${value.length} option${value.length === 1 ? "" : "s"} selected`;
      }

      const option = options.find((opt) => String(opt.value) === String(value));
      if (option != null) {
        return option.label;
      }

      if (typeof value === "string" && value.trim() !== "") {
        return value;
      }

      return placeholder;
    };

    // Get display chips for multiselect
    const getDisplayChips = () => {
      if (multiple && Array.isArray(value) && value.length > 0) {
        return (
          <Box
            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, width: "100%" }}
          >
            {value.map((val) => {
              const option = options.find(
                (opt) => String(opt.value) === String(val)
              );
              return (
                <Chip
                  key={val}
                  label={option?.label || val}
                  size="small"
                  sx={{
                    height: 24,
                    "& .MuiChip-label": {
                      px: 1,
                      fontSize: "0.8125rem",
                    },
                  }}
                />
              );
            })}
          </Box>
        );
      }
      return null;
    };

    // Determine validation icon (currently unused but kept for future use)
    // const getValidationIcon = () => {
    //   if (!showValidationIcon) return null;
    //
    //   const hasValue = multiple ? (Array.isArray(value) && value.length > 0) : (value !== '');
    //
    //   if (validationError) {
    //     return <ErrorIcon color="error" fontSize="small" />;
    //   }
    //
    //   if (hasValue && !validationError) {
    //     return <CheckIcon color="success" fontSize="small" />;
    //   }
    //
    //   return null;
    // };

    const hasError = (!deferValidation && !!validationError) || error;

    // Content to render inside ConditionalContainer
    const selectFieldContent = (
      <Wrapper sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Wrapper sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {fieldLabel && !title && (
            <Typography
              sx={{
                fontWeight: 500,
                color: "primary.main",
                fontSize: isMobile ? "14px" : "16px",
                lineHeight: isMobile ? "20px" : "22px",
              }}
            >
              {fieldLabel}
              {validation.required && (
                <Box component="span" sx={{ color: "error.main", ml: 0.5 }}>
                  *
                </Box>
              )}
            </Typography>
          )}
          {helperTop && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: isMobile ? "12px" : "14px",
                lineHeight: isMobile ? "16px" : "20px",
              }}
            >
              {helperTop}
            </Typography>
          )}
          {/* Error message above input field */}
          {((!deferValidation && validationError) || error) && (
            <Typography
              variant="body2"
              color="error"
              sx={{
                fontSize: isMobile ? "12px" : "14px",
                lineHeight: isMobile ? "16px" : "20px",
                fontWeight: 500,
              }}
            >
              {(!deferValidation && validationError) ||
                _helperText ||
                validation.requiredMessage ||
                "This field is required"}
            </Typography>
          )}
        </Wrapper>
        <StyledFormControl
          ref={ref}
          fullWidth={fullWidth}
          error={hasError}
          size={size}
          disabled={disabled}
          {...(dataPrivate && { 'data-private': true })}
        >
          <SelectTrigger
            value={getDisplayText()}
            onClick={handleDialogOpen}
            readOnly
            error={hasError}
            disabled={disabled}
            className={hasError ? "ie-hub-field-error" : ""}
            inputProps={{
              // LogRocket privacy masking - add data-private attribute for PII fields
              ...(dataPrivate && { "data-private": true }),
            }}
            endAdornment={
              <InputAdornment position="end" sx={{ gap: 0.25 }}>
                {clearable && hasSelectedValue && !disabled && (
                  <IconButton
                    size="small"
                    aria-label="Clear selection"
                    onClick={handleClearValue}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{ p: 0.5 }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
                <ExpandMoreIcon
                  sx={{
                    color: disabled ? "action.disabled" : "action.active",
                    transition: "transform 0.2s ease-in-out",
                    transform: isDialogOpen ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </InputAdornment>
            }
            sx={{
              "& .MuiOutlinedInput-input": {
                cursor: disabled ? "not-allowed" : "pointer",
              },
              // Force red border when there's a validation error
              "&.ie-hub-field-error": {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D5391F !important",
                  borderWidth: "2px !important",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D5391F !important",
                  borderWidth: "2px !important",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D5391F !important",
                  borderWidth: "2px !important",
                },
                "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D5391F !important",
                  borderWidth: "2px !important",
                },
              },
              // Fallback approach - show red border for both local validation errors and parent errors
              ...(hasError
                ? {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D5391F !important",
                      borderWidth: "2px !important",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D5391F !important",
                      borderWidth: "2px !important",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D5391F !important",
                      borderWidth: "2px !important",
                    },
                    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D5391F !important",
                      borderWidth: "2px !important",
                    },
                  }
                : {}),
            }}
          />
        </StyledFormControl>

        {/* Display selected chips for multiselect */}
        {multiple && Array.isArray(value) && value.length > 0 && (
          <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {getDisplayChips()}
          </Box>
        )}

        {helperAttached && <AttachedBlock>{helperAttached}</AttachedBlock>}
        {helperBottom && (
          <Typography
            variant="body2"
            color={
              helperBottomTone === "error" ? "error.main" : "text.secondary"
            }
            sx={{
              mt: validationError ? 0.75 : 1,
              mb: 1.5,
              fontSize: isMobile ? "12px" : "14px",
              lineHeight: isMobile ? "16px" : "20px",
            }}
          >
            {helperBottom}
          </Typography>
        )}
      </Wrapper>
    );

    const containerProps: any = {
      ifError: false, // Never show error in container
      variant: containerVariant,
      sx: {
        // Add red left border when there's a validation error (either local or from parent)
        ...(((!deferValidation && validationError) || error) && {
          borderLeft: "4px solid",
          borderLeftColor: "error.main",
          paddingLeft: 1.5,
          paddingBottom: 0, // Reduce bottom padding to minimize border height
        }),
        // Ensure consistent width regardless of error state
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch", // Changed from flex-start to stretch to maintain full width
        gap: 1,
        // Reduce overall container height when in error state
        ...(((!deferValidation && validationError) || error) && {
          paddingTop: 0,
        }),
        // Override ConditionalContainer's default title spacing
        "& > div:first-of-type": {
          mb: 0, // Reduce space below title from default 3 to 0.75
        },
      },
    };

    if (title || (fieldLabel && title !== undefined)) {
      containerProps.title = title || fieldLabel;
    }

    if (description) {
      containerProps.description = description;
    }

    return (
      <>
        <ConditionalContainer {...containerProps}>
          {selectFieldContent}
          {/* Add hidden input to ensure value is accessible via DOM query even when dialog is closed */}
          <input
            type="hidden"
            name={name}
            value={Array.isArray(value) ? value.join(",") : (value ?? "")}
          />
        </ConditionalContainer>

        {/* Selection Dialog */}
        <StyledDialog
          open={isDialogOpen}
          onClose={handleDialogClose}
          isMobile={isMobile}
          keyboardHeight={isMobile ? keyboardHeight : 0}
          {...(isMobile && { TransitionComponent: SlideUpTransition })}
          maxWidth="sm"
          fullWidth
          {...(dataPrivate && { 'data-private': true })}
        >
          {/* Dialog Header */}
          <DialogHeader isMobile={isMobile}>
            {!isMobile && (
              <IconButton
                edge="start"
                onClick={handleDialogClose}
                sx={{ mr: 2 }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}

            <Typography
              variant="h6"
              sx={{
                flex: 1,
                fontWeight: "bold",
                fontSize: isMobile ? "18px" : "20px",
                lineHeight: isMobile ? "24px" : "28px",
              }}
            >
              {customEntryMode
                ? customEntryPlaceholder || ""
                : dialogLabel || fieldLabel || "Select a Company From the List"}
            </Typography>

            <IconButton edge="end" onClick={handleDialogClose}>
              <CloseIcon />
            </IconButton>
          </DialogHeader>

          {/* Search Section */}
          {!customEntryMode && searchable && (
            <SearchContainer>
              <TextField
                inputRef={searchInputRef}
                fullWidth
                placeholder={`Try ${options[0]?.label || "searching"}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => {
                  // Scroll search input into view when focused on mobile
                  if (isMobile && searchInputRef.current) {
                    setTimeout(() => {
                      searchInputRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }, 300);
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleClearSearch}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 0, // Remove bottom margin
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    height: "42px", // Half the default height (44px -> 22px)
                  },
                  "& .MuiFormHelperText-root": {
                    display: "none", // Hide helper text if any
                  },
                }}
              />
            </SearchContainer>
          )}
          {/* Custom entry toggle link */}
          {allowCustomEntry && !customEntryMode && (
            <Box sx={{ px: 2.5, py: 1.5 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "primary.main",
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
                onClick={() => setCustomEntryMode(true)}
              >
                {customEntryLinkText}
              </Typography>
            </Box>
          )}
          {/* No Results Message */}
          {searchTerm && filteredOptions.length === 0 && (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography
                color="text.secondary"
                sx={{
                  fontSize: isMobile ? "14px" : "16px",
                  lineHeight: isMobile ? "20px" : "22px",
                }}
              >
                No results
              </Typography>
            </Box>
          )}

          {/* Options */}
          {!customEntryMode && filteredOptions.length > 0 && (
            <OptionsContainer>
              {multiple ? (
                <CheckboxGroup
                  value={Array.isArray(tempValue) ? tempValue : []}
                  onChange={handleOptionSelect}
                  options={checkboxOptions}
                  variant="outlined"
                  name={name}
                  validation={{
                    required: validation.required,
                    requiredMessage: validation.requiredMessage,
                    minSelections: validation.minSelections,
                    maxSelections: validation.maxSelections,
                  }}
                  sx={{
                    "& .MuiFormControl-root": {
                      gap: 0.5,
                    },
                    "& .option-container": {
                      marginBottom: 0.5,
                      padding: "8px 12px",
                    },
                  }}
                />
              ) : (
                <RadioGroup
                  value={tempValue}
                  onChange={handleOptionSelect}
                  options={radioOptions}
                  variant="outlined"
                  name={name}
                  sx={{
                    "& .MuiFormControl-root": {
                      gap: 0.5,
                    },
                    "& .MuiRadioGroup-root": {
                      gap: 0.5,
                    },
                    // Target the OptionContainer styled component
                    "& > div > div": {
                      marginBottom: "4px !important",
                      padding: "8px 12px !important",
                    },
                  }}
                />
              )}
            </OptionsContainer>
          )}

          {/* Custom entry form */}
          {allowCustomEntry && customEntryMode && (
            <Box
              sx={{
                p: 2.5,
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
              }}
            >
              <TextField
                fullWidth
                placeholder={customEntryPlaceholder}
                value={customEntryText}
                onChange={(e: any) => setCustomEntryText(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    height: "44px",
                  },
                }}
              />
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Button
                  color="inherit"
                  onClick={() => setCustomEntryMode(false)}
                >
                  {customEntryCancelText}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConfirmCustomEntry}
                  disabled={!customEntryText.trim()}
                >
                  {customEntryConfirmText}
                </Button>
              </Box>
            </Box>
          )}

          {/* Dialog Actions for multiselect */}
          {multiple && (
            <DialogActions
              sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}
            >
              <Button
                onClick={handleDialogClose}
                color="inherit"
                sx={{ mr: 1 }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleApplyMultiselect}
                variant="contained"
                color="primary"
              >
                {btnLabel ? btnLabel : "Apply"} (
                {Array.isArray(tempValue) ? tempValue.length : 0} selected)
              </Button>
            </DialogActions>
          )}
        </StyledDialog>
      </>
    );
  }
);

Select.displayName = "Select";

export default Select;
